import { useState } from 'react'
import { MdCheckCircle, MdFavoriteBorder, MdLockOutline, MdOutlineChatBubbleOutline, MdOutlineDescription, MdOutlineFolder, MdPlayCircleOutline, MdShare, MdStar } from 'react-icons/md'

import ChatHeader from '@/components/ChatHeader'

const CHAPTERS = [
  { id: 1, title: '第一章：人工智能基础导论', duration: '45:20', isCompleted: true, isLocked: false },
  { id: 2, title: '第二章：机器学习核心算法解析', duration: '52:15', isCompleted: true, isLocked: false },
  { id: 3, title: '第三章：深度学习与神经网络', duration: '1:05:30', isCompleted: false, isLocked: false, isPlaying: true },
  { id: 4, title: '第四章：自然语言处理进阶', duration: '48:10', isCompleted: false, isLocked: true },
  { id: 5, title: '第五章：计算机视觉应用实战', duration: '55:40', isCompleted: false, isLocked: true },
  { id: 6, title: '第六章：强化学习与前沿探索', duration: '1:12:00', isCompleted: false, isLocked: true }
]

export default function ChatCourse() {
  const [activeTab, setActiveTab] = useState('chapters')

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-gray-50 transition-all duration-300">
      <ChatHeader showTitle={false} showResourcePreview={false} />

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="mx-auto max-w-6xl">
          {/* 顶部：视频播放区域和课程信息 */}
          <div className="flex flex-col gap-4 md:gap-6 lg:flex-row">
            {/* 左侧：视频播放器 */}
            <div className="flex-1">
              <div className="relative aspect-video w-full overflow-hidden rounded-xl md:rounded-2xl bg-gray-900 shadow-lg">
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                  <MdPlayCircleOutline className="mb-2 md:mb-4 h-12 w-12 md:h-20 md:w-20 cursor-pointer text-white/80 transition-transform hover:scale-110 hover:text-white" />
                  <p className="text-base font-medium tracking-wide">第三章：深度学习与神经网络</p>
                  <p className="mt-1 md:mt-2 text-xs text-gray-400">正在播放</p>
                </div>
                {/* 进度条模拟 */}
                <div className="absolute bottom-0 left-0 right-0 h-1 md:h-1.5 bg-gray-700">
                  <div className="h-full w-1/3 bg-blue-500"></div>
                </div>
              </div>

              {/* 课程标题和操作 */}
              <div className="mt-4 md:mt-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h1 className="text-xl font-bold text-gray-900">AI 大模型全栈工程师实战营</h1>
                  <div className="mt-2 flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <MdStar className="text-yellow-400" /> 4.9 (2.5k 评价)
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span>12.5万 人已学习</span>
                    <span className="hidden sm:inline">•</span>
                    <span>讲师：李明教授</span>
                  </div>
                </div>
                <div className="flex gap-2 sm:gap-3">
                  <button className="flex h-9 md:h-10 flex-1 sm:flex-none items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50">
                    <MdFavoriteBorder className="text-lg" /> 收藏
                  </button>
                  <button className="flex h-9 md:h-10 flex-1 sm:flex-none items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50">
                    <MdShare className="text-lg" /> 分享
                  </button>
                </div>
              </div>
            </div>

            {/* 右侧：章节列表 (在桌面端显示在右侧) */}
            <div className="hidden w-80 flex-col rounded-2xl border border-gray-100 bg-white p-4 shadow-sm lg:flex">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">课程目录</h3>
              <div className="flex-1 overflow-y-auto pr-2">
                <div className="flex flex-col gap-2">
                  {CHAPTERS.map((chapter) => (
                    <div
                      key={chapter.id}
                      className={`group flex cursor-pointer items-center justify-between rounded-xl p-3 transition-colors ${
                        chapter.isPlaying ? 'bg-blue-50 border border-blue-100' : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          {chapter.isCompleted ? (
                            <MdCheckCircle className="text-lg text-green-500" />
                          ) : chapter.isPlaying ? (
                            <div className="flex h-4 w-4 items-center justify-center">
                              <span className="relative flex h-3 w-3">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75"></span>
                                <span className="relative inline-flex h-3 w-3 rounded-full bg-blue-500"></span>
                              </span>
                            </div>
                          ) : chapter.isLocked ? (
                            <MdLockOutline className="text-lg text-gray-300" />
                          ) : (
                            <MdPlayCircleOutline className="text-lg text-gray-400 group-hover:text-blue-500" />
                          )}
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${chapter.isPlaying ? 'text-blue-700' : chapter.isLocked ? 'text-gray-400' : 'text-gray-700 group-hover:text-blue-600'}`}>
                            {chapter.title}
                          </p>
                          <p className="mt-1 text-xs text-gray-400">{chapter.duration}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 底部：Tab 切换区域 */}
          <div className="mt-6 md:mt-8 rounded-xl md:rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="flex overflow-x-auto whitespace-nowrap border-b border-gray-100 no-scrollbar">
              <button
                onClick={() => setActiveTab('chapters')}
                className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-sm font-medium transition-colors lg:hidden ${
                  activeTab === 'chapters' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MdPlayCircleOutline className="text-lg" /> 目录
              </button>
              <button
                onClick={() => setActiveTab('desc')}
                className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-sm font-medium transition-colors ${
                  activeTab === 'desc' || (activeTab === 'chapters' && window.innerWidth >= 1024) ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MdOutlineDescription className="text-lg" /> 课程介绍
              </button>
              <button
                onClick={() => setActiveTab('materials')}
                className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-sm font-medium transition-colors ${
                  activeTab === 'materials' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MdOutlineFolder className="text-lg" /> 课件资料
              </button>
              <button
                onClick={() => setActiveTab('comments')}
                className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-sm font-medium transition-colors ${
                  activeTab === 'comments' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MdOutlineChatBubbleOutline className="text-lg" /> 讨论区
              </button>
            </div>

            <div className="p-4 md:p-6">
              {activeTab === 'desc' || (activeTab === 'chapters' && window.innerWidth >= 1024) ? (
                <div className="prose prose-blue max-w-none text-gray-600">
                  <h3 className="text-lg font-semibold text-gray-900">课程概述</h3>
                  <p className="mt-2 leading-relaxed">
                    本课程专为希望深入了解和掌握人工智能核心技术的开发者设计。从基础的机器学习算法到前沿的大语言模型（LLM）应用开发，通过理论结合实战的方式，带你系统性地构建 AI 知识体系。
                  </p>
                  <h3 className="mt-6 text-lg font-semibold text-gray-900">你将学到什么？</h3>
                  <ul className="mt-2 list-inside list-disc space-y-2">
                    <li>深入理解神经网络的工作原理与数学基础</li>
                    <li>熟练使用 PyTorch/TensorFlow 等主流深度学习框架</li>
                    <li>掌握 Transformer 架构及主流大模型（GPT, BERT等）的微调与部署</li>
                    <li>独立完成基于 LLM 的智能 Agent 开发实战</li>
                  </ul>
                </div>
              ) : activeTab === 'materials' ? (
                <div className="flex flex-col gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center justify-between rounded-xl border border-gray-100 p-4 transition-colors hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-500">
                          <MdOutlineFolder className="text-xl" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">第 {i} 章配套源码与数据集.zip</p>
                          <p className="text-xs text-gray-500">24.5 MB • 昨天更新</p>
                        </div>
                      </div>
                      <button className="text-sm font-medium text-blue-600 hover:text-blue-700">下载</button>
                    </div>
                  ))}
                </div>
              ) : activeTab === 'comments' ? (
                <div className="flex flex-col gap-6">
                  <div className="flex gap-4">
                    <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gradient-to-tr from-blue-400 to-purple-500"></div>
                    <div className="flex-1">
                      <textarea
                        className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        rows={3}
                        placeholder="分享你的学习心得或提出问题..."
                      ></textarea>
                      <div className="mt-2 flex justify-end">
                        <button className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700">发布评论</button>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-6 pt-4">
                    {[1, 2].map((i) => (
                      <div key={i} className="flex gap-4">
                        <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-200"></div>
                        <div>
                          <div className="flex items-baseline gap-2">
                            <span className="text-sm font-medium text-gray-900">学习者_{i}024</span>
                            <span className="text-xs text-gray-500">2小时前</span>
                          </div>
                          <p className="mt-1 text-sm text-gray-600">老师讲得非常清晰！特别是在反向传播算法的推导部分，结合代码实现让我终于弄懂了这个难点。期待后面的实战内容。</p>
                          <div className="mt-2 flex gap-4 text-xs text-gray-500">
                            <button className="hover:text-blue-600">回复</button>
                            <button className="hover:text-blue-600">点赞 (12)</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="lg:hidden">
                  <div className="flex flex-col gap-2">
                    {CHAPTERS.map((chapter) => (
                      <div
                        key={chapter.id}
                        className={`group flex cursor-pointer items-center justify-between rounded-xl p-3 transition-colors ${
                          chapter.isPlaying ? 'bg-blue-50 border border-blue-100' : 'hover:bg-gray-50 border border-transparent'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">
                            {chapter.isCompleted ? (
                              <MdCheckCircle className="text-lg text-green-500" />
                            ) : chapter.isPlaying ? (
                              <div className="flex h-4 w-4 items-center justify-center">
                                <span className="relative flex h-3 w-3">
                                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75"></span>
                                  <span className="relative inline-flex h-3 w-3 rounded-full bg-blue-500"></span>
                                </span>
                              </div>
                            ) : chapter.isLocked ? (
                              <MdLockOutline className="text-lg text-gray-300" />
                            ) : (
                              <MdPlayCircleOutline className="text-lg text-gray-400 group-hover:text-blue-500" />
                            )}
                          </div>
                          <div>
                            <p className={`text-sm font-medium ${chapter.isPlaying ? 'text-blue-700' : chapter.isLocked ? 'text-gray-400' : 'text-gray-700 group-hover:text-blue-600'}`}>
                              {chapter.title}
                            </p>
                            <p className="mt-1 text-xs text-gray-400">{chapter.duration}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
